import { Plus } from 'lucide-react';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCountryLabel } from '@components/country-select';
import { FormSection } from '@components/form-section';
import { MultiCombobox } from '@components/combobox';
import { PageHeader } from '@components/page-header';
import { Select } from '@components/select';
import { StickyBar } from '@components/sticky-bar';
import LocalizedTextArea from '@components/text-area/localized-text-area';
import { TextField } from '@components/textfield';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
} from '@components/ui/accordion';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { links } from '@core/router';
import {
    CountryCode,
    I18NText,
    ImageType,
    ProductState,
    VendorCreateRequestDto,
    VendorSource,
    VendorType
} from '@api/WalletClient';
import { WalletImageUpload } from '../components/wallet-image-upload';
import { useVendorCreateStore } from './store';

const getAllCountries = (): CountryCode[] =>
    Object.values(CountryCode).filter(
        (code) => code !== CountryCode.UNDEFINED
    ) as CountryCode[];

const CreateVendorForm = observer(() => {
    const navigate = useNavigate();
    const { createVendor } = useVendorCreateStore();

    const [formData, setFormData] = useState<VendorCreateRequestDto>({
        name: '',
        type: VendorType.VOUCHER,
        source: VendorSource.CUSTOM,
        shortDescription: { en: '' },
        description: { en: '' },
        image: { objectKey: '', bucketName: '' },
        supportedCountries: [],
        internalState: ProductState.DISABLED,
        products: []
    });

    const [redemptionDescription, setRedemptionDescription] = useState<
        I18NText | undefined
    >(undefined);
    const [error, setError] = useState<string | null>(null);

    const handleI18nTextChange =
        (field: 'description' | 'shortDescription') => (value: I18NText) =>
            setFormData((prev) => ({
                ...prev,
                [field]: value
            }));

    const handleSelectAllCountries = () => {
        setFormData((prev) => ({
            ...prev,
            supportedCountries: getAllCountries()
        }));
    };

    const isFormValid =
        formData.name.trim().length > 0 &&
        formData.image.objectKey &&
        formData.supportedCountries.length > 0;

    const handleSave = async () => {
        try {
            setError(null);
            const payload: VendorCreateRequestDto = {
                ...formData,
                redemptionDescription:
                    redemptionDescription &&
                    Object.values(redemptionDescription).some(Boolean)
                        ? redemptionDescription
                        : undefined
            };
            const created = await createVendor.request([payload]);
            if (created?.id) {
                navigate(links.vendorDetails(created.id));
            } else {
                navigate(links.vendor());
            }
        } catch (err: any) {
            console.error('Error creating vendor:', err);
            setError(
                err.response?.data?.message ||
                    err.message ||
                    'Failed to create vendor'
            );
        }
    };

    return (
        <>
            <PageHeader
                title="Create Vendor"
                description="Add a new vendor to the vouchers catalog"
            />

            <div className="flex flex-col gap-6 pb-24">
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                <FormSection title="Basic Information">
                    <div className="flex flex-col gap-4">
                        <WalletImageUpload
                            imageType={ImageType.VENDOR}
                            label="Vendor Image"
                            helperText="Upload a 16:9 image (JPG, PNG, WebP) up to 5MB"
                            initialImage={formData.image}
                            onUploaded={(image) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    image
                                }))
                            }
                            maxSizeMB={5}
                        />

                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <TextField
                                    label="Vendor Name"
                                    type="text"
                                    value={formData.name}
                                    onChange={(value) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            name: String(value)
                                        }))
                                    }
                                    required
                                />
                                <p className="mt-1 text-xs text-muted-foreground">
                                    The display name for this vendor
                                </p>
                            </div>

                            <Select
                                label="Vendor Type"
                                required
                                value={formData.type}
                                options={Object.values(VendorType).map(
                                    (type) => ({ label: type, value: type })
                                )}
                                onChange={(value) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        type: value as VendorType
                                    }))
                                }
                            />

                            <Select
                                label="Source"
                                required
                                value={formData.source}
                                options={Object.values(VendorSource).map(
                                    (source) => ({
                                        label: source,
                                        value: source
                                    })
                                )}
                                onChange={(value) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        source: value as VendorSource
                                    }))
                                }
                            />

                            <Select
                                label="Internal State"
                                required
                                value={formData.internalState}
                                options={Object.values(ProductState).map(
                                    (state) => ({ label: state, value: state })
                                )}
                                onChange={(value) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        internalState: value as ProductState
                                    }))
                                }
                            />
                        </div>

                        <div className="flex items-end gap-2">
                            <MultiCombobox<CountryCode>
                                label="Supported Countries"
                                placeholder="Select countries..."
                                required
                                value={formData.supportedCountries}
                                options={getAllCountries()}
                                getOptionLabel={(option) =>
                                    `${getCountryLabel(option)} (${option})`
                                }
                                onChange={(value) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        supportedCountries: value
                                    }))
                                }
                            />
                            <Button
                                variant="outline"
                                onClick={handleSelectAllCountries}
                            >
                                Select all
                            </Button>
                        </div>
                    </div>
                </FormSection>

                <Accordion
                    type="multiple"
                    defaultValue={['shortDescription']}
                    className="flex flex-col gap-2"
                >
                    <AccordionItem value="shortDescription">
                        <AccordionTrigger>
                            <div>
                                <p className="font-medium">Short Description</p>
                                <span className="text-xs text-muted-foreground">
                                    Brief description shown in lists and
                                    previews
                                </span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <LocalizedTextArea
                                label="Short Description"
                                value={formData.shortDescription || { en: '' }}
                                onChange={handleI18nTextChange(
                                    'shortDescription'
                                )}
                            />
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="description">
                        <AccordionTrigger>
                            <div>
                                <p className="font-medium">Full Description</p>
                                <span className="text-xs text-muted-foreground">
                                    Detailed description of the vendor and their
                                    offerings
                                </span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <LocalizedTextArea
                                label="Full Description"
                                value={formData.description || { en: '' }}
                                onChange={handleI18nTextChange('description')}
                            />
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="redemptionDescription">
                        <AccordionTrigger>
                            <div>
                                <p className="font-medium">
                                    Redemption Instructions (optional)
                                </p>
                                <span className="text-xs text-muted-foreground">
                                    Instructions for users on how to redeem
                                    vouchers
                                </span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <LocalizedTextArea
                                label="Redemption Instructions"
                                value={redemptionDescription || { en: '' }}
                                onChange={setRedemptionDescription}
                            />
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>

            <StickyBar>
                <Button
                    variant="ghost"
                    onClick={() => navigate(links.vendor())}
                    disabled={createVendor.loading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    loading={createVendor.loading}
                    disabled={createVendor.loading || !isFormValid}
                >
                    <Plus />
                    {createVendor.loading ? 'Creating...' : 'Create Vendor'}
                </Button>
            </StickyBar>
        </>
    );
});

export default CreateVendorForm;
